import React, { useCallback, useState } from 'react';
import { useDropzone, FileWithPath } from 'react-dropzone';
import s from './TravelExpensesFileUploader.module.scss';
import { IconEyeOpen, IconTrash, IconUpload } from 'components/Icons';
import {
  Box,
  FormControl,
  FormErrorMessage,
  Heading,
  List,
  ListIcon,
  ListItem,
  useDisclosure,
  Text as TextComponent,
} from '@chakra-ui/react';
import { UseFormSetValue } from 'react-hook-form';
import { FormInputs } from '../types/formInputsExpenses';
import { ImagePreviewModal } from './ImagePreviewModal';

type FileUploaderProps = {
  setValue: UseFormSetValue<FormInputs>;
  files: File[];
};

const MAX_UPLOAD_SIZE = 5242880;

export const TravelExpensesFileUploader = ({
  setValue,
  files,
}: FileUploaderProps): JSX.Element => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [imagePreview, setImagePreview] = useState('');

  // TODO: Image compression client-side?
  // D365 does not handle large files particularly well
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles =
        (!!files?.length && [...files].concat(acceptedFiles)) || acceptedFiles;
      setValue('attachments', newFiles);
    },
    [setValue, files]
  );

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 2,
    maxSize: MAX_UPLOAD_SIZE,
    accept: {
      'image/png': ['.png'],
      'image/jpg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
    },
  });

  const isFileTooLarge =
    fileRejections.length > 0 && fileRejections[0].file.size > MAX_UPLOAD_SIZE;

  return (
    <Box as="section">
      <Box as="div" {...getRootProps({ className: s.dropzoneContainer })}>
        <input {...getInputProps()} />
        <IconUpload width={50} />
        <Heading size="sm" mt={5}>
          Dragðu kvittun hingað eða ýttu á til að velja skrá
        </Heading>
      </Box>
      {files.length > 0 && (
        <List spacing={3} className={s.fileList}>
          {files.map((file, index) => (
            <ListItem key={file.name} className={s.fileItem}>
              <div className={s.previewContainer}>
                <img
                  src={URL.createObjectURL(file)}
                  alt="Preview"
                  className={s.previewImage}
                />
                <div
                  className={`${s.overlay} ${
                    hoveredIndex === index ? s.overlayTransparent : ''
                  }`}
                />
                <ListIcon
                  as={IconEyeOpen}
                  width={6}
                  height={6}
                  color={hoveredIndex === index ? 'black' : 'white'}
                  className={s.displayIcon}
                  onClick={(): void => {
                    setImagePreview(URL.createObjectURL(file));
                    onOpen();
                  }}
                  onMouseEnter={(): void => setHoveredIndex(index)}
                  onMouseLeave={(): void => setHoveredIndex(-1)}
                />
              </div>
              <div>
                <ListIcon
                  as={IconTrash}
                  color="red"
                  width={5}
                  height={5}
                  className={s.removeIcon}
                  onClick={(e): void => {
                    e.stopPropagation();
                    const newFiles = files.filter((f) => f.name !== file.name);
                    setValue('attachments', newFiles);
                  }}
                />
                {file.name}
              </div>
            </ListItem>
          ))}
        </List>
      )}
      <FormControl isInvalid={isFileTooLarge}>
        {fileRejections.map(({ file }: { file: FileWithPath }) => {
          return (
            <List key={file.path}>
              <ListItem>
                {file.path}
                <TextComponent color="warning" fontSize="sm">
                  Viðhengi verður að vera á eftirfarandi formi: .png, .jpeg,
                  .jpg, .pdf
                </TextComponent>
              </ListItem>
            </List>
          );
        })}
        {isFileTooLarge && (
          <FormErrorMessage>{`Viðhengi má ekki vera stærra en ${
            MAX_UPLOAD_SIZE / 1048576
          }MB `}</FormErrorMessage>
        )}
      </FormControl>
      <ImagePreviewModal
        onClose={onClose}
        isOpen={isOpen}
        imagePreview={imagePreview}
      />
    </Box>
  );
};
